import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { toApiParams, type ListQuery } from '@/lib/listQuery';
import { useDebouncedValue } from '@/lib/hooks/useDebouncedValue';

const EMPTY: never[] = [];
const SEARCH_DEBOUNCE_MS = 300;

interface Settled<T> {
  resource: string;
  key: string; // request key the result belongs to
  rows: T[];
  total: number;
  error: string | null;
}

export interface ServerList<T> {
  rows: T[];
  total: number;
  /** True until the first result (success or error) for this resource arrives. */
  loading: boolean;
  /** True while a user/query-driven request (query change, `refetch`) is in flight over a previous result. Background polls do not set it. */
  refreshing: boolean;
  /** Last request's error; previous rows are kept alongside it. Cleared by the next success. */
  error: string | null;
  refetch: () => void;
}

/**
 * Server-paginated list with stale-while-revalidate. Only `query.search` is debounced (300ms).
 * The effect only starts the request; state is written from promise callbacks, guarded by a
 * cleanup flag, so a response for superseded params (or after unmount) is never applied.
 * `refreshing` is derived by comparing the settled result's key with the current key. The key holds
 * the resource, params and the user `tick` (bumped by `refetch`) but NOT `pollTick` (bumped by the
 * interval): a poll re-runs the effect and replaces rows silently, so a polling list never dims or
 * shows a spinner on every interval, while query changes and `refetch()` do.
 * Superseded requests are aborted; an aborted request never sets `error` (the `cancelled` flag
 * covers the AbortError).
 *
 * Search debounce also holds back the rest of the params: while the typed search differs from the
 * debounced one (and is non-empty) the previously applied params are reused, so editing the search
 * (which resets the page to 1 in the URL) never fires a request for (old search, page 1) before
 * (new search, page 1). Filter/sort/limit/page-only changes are applied immediately.
 */
export function useServerList<T>(resource: string, query: ListQuery, opts: { intervalMs?: number } = {}): ServerList<T> {
  const { intervalMs } = opts;
  const debouncedSearch = useDebouncedValue(query.search, SEARCH_DEBOUNCE_MS);
  // An emptied search (clear filters) must not wait for the debounce.
  const effectiveSearch = query.search === '' ? '' : debouncedSearch;
  const candidateKey = JSON.stringify(toApiParams({ ...query, search: effectiveSearch }));
  const pendingSearch = effectiveSearch !== query.search;
  // Last params key that was not held back; adjusted during render (never in an effect).
  const [applied, setApplied] = useState(candidateKey);
  if (!pendingSearch && applied !== candidateKey) setApplied(candidateKey);
  const paramsKey = pendingSearch ? applied : candidateKey;

  const [tick, setTick] = useState(0);
  const [pollTick, setPollTick] = useState(0);
  const [settled, setSettled] = useState<Settled<T> | null>(null);
  const inFlight = useRef(false);

  const key = `${resource}|${paramsKey}|${tick}`;

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    inFlight.current = true;
    const reqKey = `${resource}|${paramsKey}|${tick}`; // no pollTick: polls must not read as refreshing
    const reqParams = JSON.parse(paramsKey) as Record<string, string | number>;
    api
      .paged<T>(resource, reqParams, controller.signal)
      .then(({ data, total }) => {
        if (cancelled) return;
        inFlight.current = false;
        setSettled({ resource, key: reqKey, rows: data, total, error: null });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        inFlight.current = false;
        const message = e instanceof Error ? e.message : 'Failed to load';
        setSettled((prev) => ({
          resource,
          key: reqKey,
          rows: prev?.resource === resource ? prev.rows : [],
          total: prev?.resource === resource ? prev.total : 0,
          error: message,
        }));
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [resource, paramsKey, tick, pollTick]);

  useEffect(() => {
    if (!intervalMs) return;
    const id = setInterval(() => {
      if (document.visibilityState === 'visible' && !inFlight.current) setPollTick((t) => t + 1);
    }, intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  // A result from another resource must never be shown for this one.
  const current = settled !== null && settled.resource === resource ? settled : null;
  return {
    rows: current?.rows ?? EMPTY,
    total: current?.total ?? 0,
    loading: current === null,
    refreshing: current !== null && current.key !== key,
    error: current?.error ?? null,
    refetch,
  };
}
